import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Plus, X } from 'lucide-react';
import type { CreateQuestionRequest } from '../types/Question.api.type';
import type { QuestionCreateMode } from './QuestionCreate';

interface ContentStepProps {
  handleNext: () => void;
  handlePrev: () => void;
  createQuestionParam: CreateQuestionRequest;
  setCreateQuestionParamHandler: (key: keyof CreateQuestionRequest, value: string | string[]) => void;
  isContentStepValid: boolean;
  mode?: QuestionCreateMode;
}

const ContentStep = ({
  handleNext,
  handlePrev,
  createQuestionParam,
  setCreateQuestionParamHandler,
  isContentStepValid,
  mode = 'create',
}: ContentStepProps) => {
  const isEdit = mode === 'edit';

  const addOption = () => {
    if (createQuestionParam.options.length < 5) {
      const newOptions = [...createQuestionParam.options, ''];
      setCreateQuestionParamHandler('options', newOptions);
    }
  };

  const removeOption = (index: number) => {
    if (createQuestionParam.options.length > 2) {
      const newOptions = createQuestionParam.options.filter((_, i) => i !== index);
      setCreateQuestionParamHandler('options', newOptions);
    }
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...createQuestionParam.options];
    newOptions[index] = value;
    setCreateQuestionParamHandler('options', newOptions);
  };

  /**
   * M-021: 참고 기사 링크 입력.
   * 🔴 url 만 넣으면 상세에서 안 보인다 — 표시 조건이 `referenceType &&` 이라 type 을 같이 세팅한다.
   *    비우면 type 도 함께 비운다(type 만 남으면 BE 가 "링크 주소를 입력해주세요" 로 막는다).
   */
  const setReferenceUrlHandler = (url: string) => {
    const trimmed = url.trim();
    setCreateQuestionParamHandler('referenceUrl', trimmed);
    setCreateQuestionParamHandler('referenceType', trimmed ? 'EXTERNAL' : '');
  };

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="question" className="text-sm font-medium mb-2 block">
          설문내용 (5자 이상)
        </Label>
        <Input
          id="question"
          placeholder="5자 이상 입력해주세요 (예: 오늘 출근하기 싫다.. 나만그래?)"
          value={createQuestionParam.description}
          onChange={(e) => setCreateQuestionParamHandler('description', e.target.value)}
          maxLength={100}
        />
        <div className="text-xs text-muted-foreground mt-1 text-right">
          {createQuestionParam.description.length}/100
        </div>
      </div>

      <div>
        <Label htmlFor="extraContent" className="text-sm font-medium mb-2 block">
          설문 상세내용 (선택)
        </Label>
        <Textarea
          id="extraContent"
          placeholder="설문에 도움이 될 추가 설명을 입력해주세요"
          value={createQuestionParam.questionExtraContent || ''}
          onChange={(e) => setCreateQuestionParamHandler('questionExtraContent', e.target.value)}
          rows={3}
          className="resize-none"
        />
      </div>

      {/* M-021: 참고 기사 링크 (선택) — 입력하면 상세에 "관련 기사 읽기" 버튼이 붙는다 */}
      <div>
        <Label htmlFor="referenceUrl" className="text-sm font-medium mb-2 block">
          관련 기사 링크 (선택)
        </Label>
        <Input
          id="referenceUrl"
          type="url"
          placeholder="https://"
          value={createQuestionParam.referenceUrl || ''}
          onChange={(e) => setReferenceUrlHandler(e.target.value)}
          maxLength={500}
        />
        <div className="text-xs text-muted-foreground mt-1">
          기사 주소를 넣으면 설문에 '관련 기사 읽기' 버튼이 붙어요
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-medium">
            {isEdit ? '선택지 (텍스트만 수정 가능)' : '선택지 (2-5개)'}
          </Label>
          {!isEdit && createQuestionParam.options.length < 5 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={addOption}
              className="text-primary"
              type="button"
            >
              <Plus className="w-4 h-4 mr-1" />
              선택지 추가
            </Button>
          )}
        </div>

        <div className="space-y-2">
          {createQuestionParam.options.map((option, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                placeholder={`선택지 ${index + 1}`}
                value={option}
                onChange={(e) => updateOption(index, e.target.value)}
                maxLength={100}
              />
              {!isEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeOption(index)}
                  type="button"
                  className="p-2"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 버튼 */}
      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={handlePrev}>
          뒤로
        </Button>
        <Button className="flex-1" disabled={!isContentStepValid} onClick={handleNext}>
          다음
        </Button>
      </div>
    </div>
  );
};

export default ContentStep;
